import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { TextArea } from '../../../components/TextArea';

import { Trash2, Edit3, Plus, X, ArrowUp, ArrowDown } from 'lucide-react';
import { api } from '../../../services/api';
import { enqueueSnackbar } from 'notistack';
import { GrupoTipo, CreateGrupoTipoDTO, UpdateGrupoTipoDTO, CampoTipo } from '../../../types/grupos';

interface GrupoTiposModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
  gruposTipos: GrupoTipo[];
  camposTipos: CampoTipo[];
}

interface FormData {
  nome: string;
  descricao: string;
  cor_identificacao: string;
  permite_subgrupos: boolean;
  requer_aprovacao: boolean;
  nivel_aprovacao: number;
  campos_obrigatorios: string[];
  campos_opcionais: string[];
}

const initialFormData: FormData = {
  nome: '',
  descricao: '',
  cor_identificacao: '#3B82F6',
  permite_subgrupos: false,
  requer_aprovacao: false,
  nivel_aprovacao: 1,
  campos_obrigatorios: [],
  campos_opcionais: []
};

const GrupoTiposModal: React.FC<GrupoTiposModalProps> = ({
  isOpen,
  onClose,
  onUpdate,
  gruposTipos,
  camposTipos
}) => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const moveTipo = async (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === gruposTipos.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const newTipos = [...gruposTipos];
    const [removed] = newTipos.splice(index, 1);
    newTipos.splice(newIndex, 0, removed);

    try {
      const ordens = newTipos.map((tipo, idx) => ({
        id: tipo.id,
        ordem: idx + 1
      }));

      await api.patch('/grupos-tipos/ordem', { ordens });
      enqueueSnackbar('Ordem atualizada com sucesso', { variant: 'success' });
      onUpdate();
    } catch (error) {
      enqueueSnackbar('Erro ao atualizar ordem', { variant: 'error' });
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setFormData(initialFormData);
      setEditingId(null);
      setShowForm(false);
    }
  }, [isOpen]);

  const handleEdit = (tipo: GrupoTipo) => {
    setFormData({
      nome: tipo.nome,
      descricao: tipo.descricao || '',
      cor_identificacao: tipo.cor_identificacao,
      permite_subgrupos: tipo.permite_subgrupos,
      requer_aprovacao: tipo.requer_aprovacao,
      nivel_aprovacao: tipo.nivel_aprovacao,
      campos_obrigatorios: tipo.campos_obrigatorios || [],
      campos_opcionais: tipo.campos_opcionais || []
    });
    setEditingId(tipo.id);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja deletar este tipo de grupo?')) return;

    try {
      await api.delete(`/grupos-tipos/${id}`);
      enqueueSnackbar('Tipo de grupo deletado com sucesso!', { variant: 'success' });
      onUpdate();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Erro ao deletar tipo de grupo', { variant: 'error' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      if (editingId) {
        const updateData: UpdateGrupoTipoDTO = {
          ...formData
        };
        await api.put(`/grupos-tipos/${editingId}`, updateData);
        enqueueSnackbar('Tipo de grupo atualizado com sucesso!', { variant: 'success' });
      } else {
        const createData: CreateGrupoTipoDTO = {
          ...formData
        };
        await api.post('/grupos-tipos', createData);
        enqueueSnackbar('Tipo de grupo criado com sucesso!', { variant: 'success' });
      }

      setFormData(initialFormData);
      setEditingId(null);
      setShowForm(false);
      onUpdate();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Erro ao salvar tipo de grupo', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCampoToggle = (campoId: string, listKey: 'campos_obrigatorios' | 'campos_opcionais') => {
    const currentList = formData[listKey];
    const otherListKey = listKey === 'campos_obrigatorios' ? 'campos_opcionais' : 'campos_obrigatorios';
    const otherList = formData[otherListKey];

    if (currentList.includes(campoId)) {
      // Remove da lista atual
      setFormData(prev => ({
        ...prev,
        [listKey]: currentList.filter(id => id !== campoId)
      }));
    } else {
      // Remove da outra lista se estiver lá e adiciona na atual
      setFormData(prev => ({
        ...prev,
        [listKey]: [...currentList, campoId],
        [otherListKey]: otherList.filter(id => id !== campoId)
      }));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Gerenciar Tipos de Grupos</DialogTitle>
          <DialogDescription>
            Configure os tipos de grupos disponíveis no sistema e suas características.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Botão para adicionar novo tipo */}
          {!showForm && (
            <Button
              onClick={() => setShowForm(true)}
              className="w-full"
              variant="outline"
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Novo Tipo de Grupo
            </Button>
          )}

          {/* Formulário */}
          {showForm && (
            <div className="border border-border rounded-lg p-4 bg-muted/50">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium">
                  {editingId ? 'Editar Tipo de Grupo' : 'Novo Tipo de Grupo'}
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowForm(false);
                    setFormData(initialFormData);
                    setEditingId(null);
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="nome">Nome do Tipo *</Label>
                    <Input
                      id="nome"
                      value={formData.nome}
                      onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                      placeholder="Ex: Companhia, Pelotão, Esquadrão..."
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cor">Cor de Identificação</Label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        id="cor"
                        value={formData.cor_identificacao}
                        onChange={(e) => setFormData(prev => ({ ...prev, cor_identificacao: e.target.value }))}
                        className="w-12 h-10 rounded border"
                      />
                      <Input
                        value={formData.cor_identificacao}
                        onChange={(e) => setFormData(prev => ({ ...prev, cor_identificacao: e.target.value }))}
                        placeholder="#3B82F6"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <TextArea
                    name="descricao"
                    title="Descrição"
                    value={formData.descricao}
                    onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                    placeholder="Descreva as características deste tipo de grupo..."
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="permite_subgrupos"
                      checked={formData.permite_subgrupos}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, permite_subgrupos: checked }))}
                    />
                    <Label htmlFor="permite_subgrupos">Permite Subgrupos</Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="requer_aprovacao"
                      checked={formData.requer_aprovacao}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, requer_aprovacao: checked }))}
                    />
                    <Label htmlFor="requer_aprovacao">Requer Aprovação</Label>
                  </div>
                </div>

                  {formData.requer_aprovacao && (
                  <div className="space-y-2">
                    <Label htmlFor="nivel_aprovacao">Nível de Aprovação</Label>
                    <select
                      id="nivel_aprovacao"
                      value={formData.nivel_aprovacao.toString()}
                      onChange={(e) => setFormData(prev => ({ ...prev, nivel_aprovacao: parseInt(e.target.value) }))}
                      className="flex h-10 w-full appearance-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Selecione o nível</option>
                      <option value="1">Nível 1 - Básico</option>
                      <option value="2">Nível 2 - Intermediário</option>
                      <option value="3">Nível 3 - Avançado</option>
                      <option value="4">Nível 4 - Superior</option>
                      <option value="5">Nível 5 - Máximo</option>
                    </select>
                  </div>
                )}                {/* Configuração de Campos */}
                {camposTipos.length > 0 && (
                  <div className="space-y-4">
                    <h4 className="font-medium">Configuração de Campos</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-red-600">Campos Obrigatórios</Label>
                        <div className="mt-2 space-y-2 max-h-32 overflow-y-auto border border-border rounded p-2 bg-background">
                          {camposTipos.map(campo => (
                            <div key={`obrig-${campo.id}`} className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                id={`obrig-${campo.id}`}
                                checked={formData.campos_obrigatorios.includes(campo.id)}
                                onChange={() => handleCampoToggle(campo.id, 'campos_obrigatorios')}
                                className="rounded"
                              />
                              <Label htmlFor={`obrig-${campo.id}`} className="text-sm">
                                {campo.nome}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-blue-600">Campos Opcionais</Label>
                        <div className="mt-2 space-y-2 max-h-32 overflow-y-auto border border-border rounded p-2 bg-background">
                          {camposTipos.map(campo => (
                            <div key={`opc-${campo.id}`} className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                id={`opc-${campo.id}`}
                                checked={formData.campos_opcionais.includes(campo.id)}
                                onChange={() => handleCampoToggle(campo.id, 'campos_opcionais')}
                                className="rounded"
                              />
                              <Label htmlFor={`opc-${campo.id}`} className="text-sm">
                                {campo.nome}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-2 pt-4 border-t border-border">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowForm(false);
                      setFormData(initialFormData);
                      setEditingId(null);
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Lista de tipos existentes */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Tipos Cadastrados ({gruposTipos.length})</h3>

            {gruposTipos.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum tipo de grupo cadastrado ainda.
              </div>
            ) : (
              <div className="grid gap-4">
                {gruposTipos.map((tipo, index) => (
                  <div key={tipo.id} className="border border-border rounded-lg p-4 bg-card dark:bg-muted/30 flex">
                    {/* Botões de reordenação */}
                    <div className="flex flex-col gap-1 mr-3 justify-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => moveTipo(index, 'up')}
                        disabled={index === 0}
                        className="h-6 w-6 p-0"
                      >
                        <ArrowUp className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => moveTipo(index, 'down')}
                        disabled={index === gruposTipos.length - 1}
                        className="h-6 w-6 p-0"
                      >
                        <ArrowDown className="h-3 w-3" />
                      </Button>
                    </div>

                    <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <div
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: tipo.cor_identificacao }}
                          />
                          <h4 className="font-medium">{tipo.nome}</h4>
                          {tipo.requer_aprovacao && (
                            <Badge variant="outline" className="text-xs">
                              Aprovação Nível {tipo.nivel_aprovacao}
                            </Badge>
                          )}
                          {tipo.permite_subgrupos && (
                            <Badge variant="outline" className="text-xs">
                              Permite Subgrupos
                            </Badge>
                          )}
                        </div>

                        {tipo.descricao && (
                          <p className="text-sm text-muted-foreground mb-2">{tipo.descricao}</p>
                        )}

                        <div className="flex flex-wrap gap-2 text-xs">
                          {tipo.campos_obrigatorios && tipo.campos_obrigatorios.length > 0 && (
                            <div className="flex items-center space-x-1">
                              <span className="text-red-600 font-medium">Obrigatórios:</span>
                              <span className="text-red-600">
                                {tipo.campos_obrigatorios.length} campo(s)
                              </span>
                            </div>
                          )}
                          {tipo.campos_opcionais && tipo.campos_opcionais.length > 0 && (
                            <div className="flex items-center space-x-1">
                              <span className="text-blue-600 font-medium">Opcionais:</span>
                              <span className="text-blue-600">
                                {tipo.campos_opcionais.length} campo(s)
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(tipo)}
                        >
                          <Edit3 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(tipo.id)}
                          className="text-red-600 hover:text-red-700 hover:border-red-300 dark:hover:bg-red-900/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>                    </div>                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GrupoTiposModal;
